import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

const NewsList = ({ status, onEdit }) => {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchArticles();
    }
  }, [user, status]);

  const fetchArticles = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase?.from('provider_news')?.select('*')?.eq('provider_id', user?.id)?.eq('status', status)?.order('created_at', { ascending: false });

      if (error) throw error;
      setArticles(data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (!confirm('Weet u zeker dat u dit artikel wilt verwijderen?')) return;

    try {
      const { error } = await supabase?.from('provider_news')?.delete()?.eq('id', articleId)?.eq('provider_id', user?.id);

      if (error) throw error;
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      alert('Fout bij verwijderen van artikel');
    }
  };

  const handlePublish = async (article) => {
    try {
      const { error } = await supabase?.from('provider_news')?.update({
          status: 'published',
          published_at: new Date()?.toISOString(),
        })?.eq('id', article?.id)?.eq('provider_id', user?.id);

      if (error) throw error;
      fetchArticles();
    } catch (error) {
      console.error('Error publishing article:', error);
      alert('Fout bij publiceren van artikel');
    }
  };

  const handleUnpublish = async (article) => {
    try {
      const { error } = await supabase?.from('provider_news')?.update({
          status: 'draft',
          published_at: null,
        })?.eq('id', article?.id)?.eq('provider_id', user?.id);

      if (error) throw error;
      fetchArticles();
    } catch (error) {
      console.error('Error unpublishing article:', error);
      alert('Fout bij depubliceren van artikel');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3]?.map((i) => (
          <div key={i} className="border border-border rounded-lg p-6 animate-pulse">
            <div className="h-6 bg-muted rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-muted rounded w-full mb-2"></div>
            <div className="h-4 bg-muted rounded w-5/6"></div>
          </div>
        ))}
      </div>
    );
  }

  if (articles?.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
          <span className="text-3xl">📰</span>
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {status === 'published' ? 'Geen gepubliceerde artikelen' : 'Geen concepten'}
        </h3>
        <p className="text-muted-foreground">
          {status === 'published' ?'Publiceer uw eerste artikel om uw zichtbaarheid te vergroten' :'Begin met schrijven om uw eerste artikel te maken'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {articles?.map((article) => (
        <div
          key={article?.id}
          className="border border-border rounded-lg p-6 hover:shadow-elevation-1 transition-shadow"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-2">{article?.title}</h3>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {article?.excerpt || article?.content?.substring(0, 150) + '...'}
              </p>
              <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                <span>
                  Aangemaakt: {format(new Date(article?.created_at), 'dd MMM yyyy', { locale: nl })}
                </span>
                {article?.published_at && (
                  <span>
                    Gepubliceerd: {format(new Date(article?.published_at), 'dd MMM yyyy', { locale: nl })}
                  </span>
                )}
                <span>Weergaven: {article?.view_count || 0}</span>
              </div>
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <Button
                size="sm"
                variant="outline"
                iconName="Edit"
                onClick={() => onEdit(article)}
              >
                Bewerken
              </Button>
              {status === 'draft' ? (
                <Button
                  size="sm"
                  variant="default"
                  iconName="Send"
                  onClick={() => handlePublish(article)}
                >
                  Publiceren
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  iconName="Archive"
                  onClick={() => handleUnpublish(article)}
                >
                  Depubliceren
                </Button>
              )}
              <Button
                size="sm"
                variant="destructive"
                iconName="Trash2"
                onClick={() => handleDelete(article?.id)}
              >
                Verwijderen
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NewsList;